import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowUpRight, ShieldAlert, ShieldCheck } from "lucide-react";
import { verificationTier, verificationMeta } from "../lib/verification";
import StatusBadge from "./StatusBadge";

const isPendingCategory = (abuser) => abuser?.disposition?.category === "pending";

export default function AbuserCard({ abuser, idx = 0 }) {
  const tier = verificationTier(abuser);
  const vmeta = verificationMeta(tier);
  return (
    <Link
      to={`/abuser/${abuser.id}`}
      data-testid={`abuser-card-${abuser.id}`}
      className="group block border border-zinc-300 bg-white hover-shadow-harsh fade-up p-5"
      style={{ animationDelay: `${Math.min(idx, 20) * 40}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow flex items-center gap-1.5">
            <span className="truncate">{abuser.case_id}</span>
            {tier === "official" && (
              <span
                className="inline-flex items-center gap-0.5 text-green-600 normal-case tracking-normal font-semibold"
                title={vmeta.description}
                data-testid={`abuser-verified-${abuser.id}`}
              >
                <ShieldCheck size={11} />
              </span>
            )}
          </div>
          <h3 className="font-display font-black text-xl tracking-tight mt-1 truncate">
            {abuser.name}
          </h3>
          {abuser.aliases && abuser.aliases.length > 0 && (
            <div className="text-[11px] font-mono text-zinc-500 mt-1 truncate">
              aka {abuser.aliases.join(", ")}
            </div>
          )}
        </div>
        <ArrowUpRight
          size={18}
          className="text-zinc-400 group-hover:text-red-600 transition-colors flex-shrink-0"
        />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="badge-crime">{abuser.crime_type}</span>
        <StatusBadge disposition={abuser.disposition} size="sm" />
        {abuser.reoffender && (
          <span
            className="inline-flex items-center gap-1 bg-red-600 text-white border-2 border-red-700 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_rgba(0,0,0,0.85)]"
            title="This person has prior animal-cruelty convictions or a lifetime animal-ownership ban."
            data-testid={`abuser-reoffender-${abuser.id}`}
          >
            <ShieldAlert size={11} strokeWidth={2.5} /> Repeat Offender
          </span>
        )}
        {abuser.distance_mi !== undefined && (
          <span className="badge-neutral bg-zinc-900 text-white border-zinc-900" data-testid={`abuser-distance-${abuser.id}`}>
            {abuser.distance_mi} mi away
          </span>
        )}
      </div>

      <p className="mt-4 text-sm text-zinc-700 leading-relaxed line-clamp-2">
        {abuser.crime_summary}
      </p>

      <div className="mt-5 pt-4 border-t border-zinc-200 grid grid-cols-2 gap-4">
        <div>
          <div className="label-eyebrow mb-1">Jurisdiction</div>
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            <MapPin size={13} strokeWidth={2} />
            <span className="truncate">{abuser.city}, {abuser.state_code}</span>
          </div>
        </div>
        <div>
          <div className="label-eyebrow mb-1">{isPendingCategory(abuser) ? "Filed" : "Convicted"}</div>
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            <Calendar size={13} strokeWidth={2} />
            {abuser.conviction_year}
          </div>
        </div>
      </div>
    </Link>
  );
}
